import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, BookOpen, TrendingUp, Clock, CheckCircle, XCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import ReceiptModal from '../components/ReceiptModal';
import { enrollments, progress } from '../lib/api';
import { removeToken, getUser } from '../lib/auth';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [myProgress, setMyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptData, setReceiptData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [enrollmentsRes, progressRes] = await Promise.all([
        enrollments.getMy(),
        progress.getMy(),
      ]);
      setMyEnrollments(enrollmentsRes.data);
      setMyProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    navigate('/');
  };

  const getProgressForCourse = (courseId) => {
    return myProgress.find((p) => p.course_id === courseId);
  };

  const handleViewReceipt = async (enrollmentId) => {
    try {
      const res = await enrollments.getReceipt(enrollmentId);
      setReceiptData(res.data);
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo size="sm" />
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/courses')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
                data-testid="browse-courses-btn"
              >
                Browse Courses
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 flex items-center gap-2 text-gray-700 hover:text-gray-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome back, {user?.name}!</h1>
          <p className="text-lg text-gray-600">Track your learning progress</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-black" />
                  <h3 className="font-semibold text-gray-900">Total Enrollments</h3>
                </div>
                <p className="text-4xl font-bold text-gray-900">{myEnrollments.length}</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h3 className="font-semibold text-gray-900">Verified</h3>
                </div>
                <p className="text-4xl font-bold text-gray-900">
                  {myEnrollments.filter((e) => e.payment_verified).length}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-6 h-6 text-amber-600" />
                  <h3 className="font-semibold text-gray-900">Pending</h3>
                </div>
                <p className="text-4xl font-bold text-gray-900">
                  {myEnrollments.filter((e) => !e.payment_verified).length}
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">My Courses</h2>

              {myEnrollments.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-gray-200">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No enrollments yet</h3>
                  <p className="text-gray-600 mb-6">Start your German learning journey today!</p>
                  <button
                    onClick={() => navigate('/courses')}
                    className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                    data-testid="browse-courses-cta"
                  >
                    Browse Courses
                  </button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {myEnrollments.map((enrollment) => {
                    const courseProgress = getProgressForCourse(enrollment.course_id);
                    return (
                      <div
                        key={enrollment.id}
                        className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200"
                        data-testid={`enrollment-card-${enrollment.course_level}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <span className="inline-block px-3 py-1 bg-black text-white text-xs font-mono uppercase rounded-full mb-2">
                              {enrollment.course_level}
                            </span>
                            <h3 className="text-xl font-bold text-gray-900">{enrollment.course_title}</h3>
                          </div>
                          {enrollment.payment_verified ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <XCircle className="w-6 h-6 text-amber-600" />
                          )}
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                            <span>Progress</span>
                            <span className="font-semibold">{courseProgress?.completion_percentage || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-black h-2 rounded-full transition-all"
                              style={{ width: `${courseProgress?.completion_percentage || 0}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <div>
                            <p className="text-sm text-gray-600">Amount Paid</p>
                            <p className="text-lg font-bold text-gray-900">
                              {enrollment.payment_method === 'upi'
                                ? `INR ${(enrollment.amount_inr || enrollment.amount_pi * 356).toLocaleString('en-IN')}`
                                : `${enrollment.amount_pi} π`}
                            </p>
                            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              enrollment.payment_method === 'upi' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {enrollment.payment_method === 'upi' ? 'UPI' : 'Pi'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {(enrollment.payment_verified || enrollment.cancelled) && (
                              <button
                                onClick={() => handleViewReceipt(enrollment.id)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                                  enrollment.cancelled
                                    ? 'bg-red-700 text-white hover:bg-red-800'
                                    : 'bg-black text-white hover:bg-gray-800'
                                }`}
                                data-testid={`receipt-btn-${enrollment.course_level}`}
                              >
                                <FileText className="w-4 h-4" />
                                {enrollment.cancelled ? 'Cancellation Receipt' : 'Receipt'}
                              </button>
                            )}
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                enrollment.cancelled
                                  ? 'bg-red-100 text-red-700'
                                  : enrollment.payment_verified
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {enrollment.cancelled ? 'Cancelled' : enrollment.payment_verified ? 'Verified' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {receiptData && (
        <ReceiptModal receipt={receiptData} onClose={() => setReceiptData(null)} />
      )}
    </div>
  );
};

export default StudentDashboard;
